import * as React from 'react';

import {
  Button,
  ContentSection,
  LocalizedDate,
  TextField,
} from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import isEqual from 'lodash/isEqual';
import { Plus } from 'lucide-react';

import {
  $addCategoryAttribute,
  $categoryForm,
  $removeCategoryAttribute,
  $updateCategoryAttribute,
  CategoryAttributeFormState,
  CategoryFormState,
} from '../../../_state/categoryForm.state';
import { useTranslations } from '../../../i18n/useTranslations';
import { CategoryRecord } from '../../../types/inventoryItem';

import { CategoryAttributeItem } from './CategoryAttributeItem';

type CategoryFormFieldsProps = {
  disabled?: boolean;
  showTimestamps?: boolean;
  category?: CategoryRecord | null;
  className?: string;
};

const formSelector = (state: CategoryFormState) => ({
  name: state.name,
  description: state.description,
  attributes: state.attributes,
});

export const CategoryFormFields = ({
  disabled = false,
  showTimestamps = false,
  category,
  className,
}: CategoryFormFieldsProps) => {
  const t = useTranslations();

  const selectedFormAtom = React.useMemo(
    () => selectAtom($categoryForm, formSelector, isEqual),
    [],
  );

  const form = useAtomValue(selectedFormAtom);
  const updateForm = useSetAtom($categoryForm);
  const addAttribute = useSetAtom($addCategoryAttribute);
  const removeAttribute = useSetAtom($removeCategoryAttribute);
  const updateAttribute = useSetAtom($updateCategoryAttribute);

  const handleFieldChange = React.useCallback(
    <K extends keyof CategoryFormState>(field: K) =>
      (value: CategoryFormState[K]) => {
        updateForm((prev) => ({
          ...prev,
          [field]: value,
        }));
      },
    [updateForm],
  );

  const handleAttributeChange = React.useCallback(
    (
      index: number,
      field: keyof CategoryAttributeFormState,
      value: string | boolean | string[] | null,
    ) => {
      updateAttribute({ index, field, value });
    },
    [updateAttribute],
  );

  return (
    <div className={cn('@CategoryFormFields', className)}>
      <ContentSection
        title={t.inventoryView.categoryDetailView.sections.categoryInformation}
        variant="panel"
      >
        <div className="grid gap-4">
          <TextField
            label={t.inventoryView.categoryCreateDialog.fields.name}
            value={form.name}
            onChange={(value) => handleFieldChange('name')(value)}
            placeholder={t.inventoryView.categoryCreateDialog.placeholders.name}
            isRequired
            isDisabled={disabled}
          />

          <TextField
            label={t.inventoryView.categoryCreateDialog.fields.description}
            value={form.description}
            onChange={(value) => handleFieldChange('description')(value)}
            placeholder={
              t.inventoryView.categoryCreateDialog.placeholders.description
            }
            isDisabled={disabled}
          />
        </div>
      </ContentSection>

      {showTimestamps && (
        <ContentSection
          title={t.inventoryView.categoryDetailView.sections.timestamps}
          variant="panel"
        >
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-1">
              <span className="text-sm text-neutral/60">
                {t.inventoryView.categoryDetailView.fields.created}
              </span>
              <span className="text-sm font-medium">
                {category?.timestamps.createdAt ? (
                  <LocalizedDate date={category.timestamps.createdAt} />
                ) : (
                  '-'
                )}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-sm text-neutral/60">
                {t.inventoryView.categoryDetailView.fields.updated}
              </span>
              <span className="text-sm font-medium">
                {category?.timestamps.updatedAt ? (
                  <LocalizedDate date={category.timestamps.updatedAt} />
                ) : (
                  '-'
                )}
              </span>
            </div>
          </div>
        </ContentSection>
      )}

      <ContentSection
        title={t.inventoryView.categoryDetailView.sections.attributes}
        variant="panel"
        hideDivider
      >
        {form.attributes.length === 0 ? (
          <div className="py-4 text-center text-sm text-neutral/50">
            {t.inventoryView.categoryCreateDialog.messages.noAttributes}
          </div>
        ) : (
          <div className="space-y-4">
            {form.attributes.map((attr, index) => (
              <CategoryAttributeItem
                key={index}
                attribute={attr}
                index={index}
                onUpdate={handleAttributeChange}
                onRemove={removeAttribute}
                disabled={disabled}
              />
            ))}
          </div>
        )}
        <Button
          variant="outline"
          size="large"
          icon={<Plus />}
          onPress={() => addAttribute()}
          isDisabled={disabled}
          className="mt-3 max-w-40"
        >
          {t.inventoryView.categoryCreateDialog.actions.addAttribute}
        </Button>
      </ContentSection>
    </div>
  );
};
